import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Phone, Car, Hash, Ticket, ChevronRight, X } from 'lucide-react';
import { customerAPI } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { useEffect } from 'react';
import toast from 'react-hot-toast';

const CustomerCard = ({ customer, onClick }) => (
  <button
    onClick={() => onClick(customer)}
    className="w-full text-left bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:border-brand-400 dark:hover:border-brand-600 hover:shadow-md transition-all duration-150 group"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-brand-100 dark:bg-brand-900/30 rounded-full flex items-center justify-center text-brand-700 dark:text-brand-400 font-bold text-sm flex-shrink-0">
          {customer.customer_name?.charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-gray-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
            {customer.customer_name}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Hash className="w-3 h-3" />
              {customer.membership_no}
            </span>
            {customer.mobile && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Phone className="w-3 h-3" />
                {customer.mobile}
              </span>
            )}
            {customer.vehicle_no && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Car className="w-3 h-3" />
                {customer.vehicle_no}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 border border-brand-200 dark:border-brand-800">
          {customer.coupon_type || 'Coupon'}
        </span>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-500 transition-colors" />
      </div>
    </div>
  </button>
);

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debouncedQuery = useDebounce(query, 400);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    doSearch(debouncedQuery);
  }, [debouncedQuery]);

  const doSearch = async (q) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await customerAPI.search(q);
      setResults(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (customer) => {
    navigate(`/customer/${customer.id}`);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
    inputRef.current?.focus();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Search Customer</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Find customers by name, membership number, mobile, vehicle number, or coupon number
        </p>
      </div>

      {/* Search box */}
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
          {loading
            ? <div className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
            : <Search className="w-5 h-5" />
          }
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name, membership no, mobile, vehicle no, or coupon no..."
          className="w-full pl-12 pr-10 py-3.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm shadow-sm"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search hints */}
      {!searched && !query && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { icon: User, label: 'Customer Name', example: 'e.g. Ramesh Kumar' },
            { icon: Hash, label: 'Membership No', example: 'e.g. MEM001' },
            { icon: Phone, label: 'Mobile Number', example: 'e.g. 9876543210' },
            { icon: Car, label: 'Vehicle Number', example: 'e.g. GJ01AB1234' },
            { icon: Ticket, label: 'Coupon Number', example: 'e.g. CPN001' },
          ].map(({ icon: Icon, label, example }) => (
            <div key={label} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3.5">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-brand-500" />
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</span>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">{example}</div>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {searched && !loading && (
        <>
          {results.length > 0 ? (
            <div className="space-y-3">
              <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                {results.length} customer{results.length !== 1 ? 's' : ''} found
              </div>
              {results.map(c => (
                <CustomerCard key={c.id} customer={c} onClick={handleSelect} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">No customers found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try a different search term</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;
